// The Document part's screens in Me: the nav item, /me/documents (the list,
// beside the open document), /me/documents/new and /me/documents/<key>, the
// realtime handlers, the favorite kind `page` and a document shown in a side
// view. The same tree of routes mounts under a host's own list
// (`documentsRoutes`: cloud's /<slug>/documents), with that list beside the
// documents and its own way of making them.
//
// Promise:
// - A document opens by its key. A path naming its id (favorites point at
//   ids) is replaced by the key's path, so the URL that is shared is one.
// - A document opens under the list it belongs to: one opened under another
//   (a personal document under a host's list, a shared one under Me's) is sent
//   to its own (the host says where shared ones live: `placeBase`).
// - The body's text never comes through events: it arrives through live
//   editing. Events refresh the list, the details and the history only.
import { type QueryClient, useQuery } from "@tanstack/react-query";
import {
  type AnyRoute,
  createRoute,
  Outlet,
  redirect,
  useChildMatches,
  useParams,
} from "@tanstack/react-router";
import {
  ME_DATASET_REF_PATHS,
  meDatasetPath,
} from "@trameria/core/datasets-shared";
import {
  documentPath,
  ME_DOCUMENT_REF_PATHS,
  type PageDTO,
} from "@trameria/core/documents-shared";
import { m } from "@trameria/i18n";
import { type ComponentType, type ReactNode, use, useMemo } from "react";
import { useApi } from "../app/context";
import { lazyScreen, type Preloadable, withLazy } from "../app/lazy";
import type { MeRouterContext } from "../app/routes";
import {
  defineScreenPart,
  type ScreenPart,
  type SideViewProps,
} from "../app/screen-part";
import { meDatasetsRoutes } from "../datasets/personal-routes";
import { invalidateMemo } from "../memos/queries";
import { type ResolvedRef, refPrefix, refQuery } from "../refs/queries";
import { ListDetail, useShowsDetail } from "../shell/list-detail";
import { iconOf } from "../ui/icon";
import { type DocumentBlockPart, DocumentBlockPartsContext } from "./blocks";
import { type DocumentsHome, DocumentsHomeContext, ME_HOME } from "./home";
import { NEW_DOCUMENT_KEY, NEW_INDEX_KEY } from "./indexes/keys";
import { NewHereScope, useIndexPlace } from "./indexes/new-here";
import { indexesPrefix, indexListKey, treeQuery } from "./indexes/queries";
import { indexUpPath } from "./indexes/up-path";
import type { LiveTransport } from "./live";
import {
  DocumentPathsContext,
  DocumentPlaceContext,
  documentPaths,
  listPlace,
  ME_DOCUMENTS,
  useDocumentPaths,
} from "./place";
import {
  detailKey,
  detailQuery,
  documentsPrefix,
  invalidateDetailsOf,
  listPrefix,
  noteRevision,
  uiAnswersKey,
  versionsPrefix,
} from "./queries";

// The screens are loaded when first opened (app/lazy.tsx).
const DocumentFrame = lazyScreen(
  () => import("./document-screen"),
  "DocumentFrame",
);
const DocumentView = lazyScreen(
  () => import("./document-screen"),
  "DocumentView",
);
const IndexColumn = lazyScreen(
  () => import("./indexes/tree-column"),
  "IndexColumn",
);
const DocumentList = lazyScreen(() => import("./list"), "DocumentList");

export interface DocumentsScreenOptions {
  /** How this host reaches live editing (the host decides the transport). */
  live: LiveTransport;
  /**
   * What other parts add to the document body (blocks/block-parts.ts): the
   * ui fence runtime's `uiFenceDocumentBlocks` makes ```ui blocks run. Or a
   * function that loads them, called with the document screens' code
   * (`loadUiFenceDocumentBlocks`), so they stay out of the app's first file.
   */
  blocks?:
    | readonly DocumentBlockPart[]
    | (() => Promise<readonly DocumentBlockPart[]>);
  /**
   * Where a shared document (one without an owner) is listed, from what a
   * reference to it says beyond its key (cloud: `{ workspace }` gives
   * "/<slug>/documents"); null when it is not known. Without it, shared
   * documents open where they are asked for.
   */
  placeBase?(path: Readonly<Record<string, unknown>>): string | null;
  /**
   * Paths of the host's lists that name a document, as `refPaths` writes them
   * (cloud: "/:slug/documents/:key"). A pasted URL of one becomes a reference
   * as Me's do.
   */
  refPaths?: readonly string[];
}

/** A list of documents the screens mount beside (Me's, or a host's). */
export interface DocumentsMount {
  /** The route's path under its parent ("documents"). */
  path: string;
  /** The list's path from the route's params ("/me/documents", "/acme/documents"). */
  base(params: Readonly<Record<string, string>>): string;
  /** The list column, beside the open document or alone. */
  List: ComponentType<{ openKey: string | null }>;
  /** The list's name (where the phone's way up goes). */
  listLabel(): string;
  /** How the list makes documents, what its indexes take, its header's addition. */
  home: DocumentsHome;
  /** Whether a document is this list's (one that is not is sent to its own). */
  holds(page: Pick<PageDTO, "ownerUserId">): boolean;
}

/**
 * The list of documents beside the open document (or alone when none is
 * open), as Cases and People: the line between them is a handle, and the list
 * folds away for the full width. Under <list>/index/<key> the list is that
 * index's tree, and the phone's way up from a document is its parent's row.
 */
function DocumentsScreen({
  mount,
  indexDetailRoutes,
}: {
  mount: DocumentsMount;
  /** The routes under an index that open a document (or a new one). */
  indexDetailRoutes: () => readonly string[];
}) {
  const paths = useDocumentPaths();
  const { key, indexKey } = useParams({ strict: false }) as {
    key?: string;
    indexKey?: string;
  };
  const showing = useShowsDetail();
  const inIndexDetail = useChildMatches({
    select: (matches) =>
      matches.some((x) => indexDetailRoutes().includes(x.routeId)),
  });
  if (indexKey !== undefined)
    return (
      <NewHereScope key={indexKey}>
        <IndexScreen
          indexKey={indexKey}
          openKey={key ?? null}
          showing={inIndexDetail}
        />
      </NewHereScope>
    );
  const List = mount.List;
  return (
    <ListDetail
      list={<List openKey={key ?? null} />}
      detail={<Outlet />}
      showing={showing ? "detail" : "list"}
      listPath={paths.list}
      listLabel={mount.listLabel()}
    />
  );
}

function IndexScreen({
  indexKey,
  openKey,
  showing,
}: {
  indexKey: string;
  openKey: string | null;
  showing: boolean;
}) {
  const api = useApi();
  const paths = useDocumentPaths();
  const tree = useQuery({
    ...treeQuery(api, indexKey),
    enabled: indexKey !== NEW_INDEX_KEY,
  });
  return (
    <ListDetail
      list={<IndexColumn indexKey={indexKey} openKey={openKey} />}
      detail={<Outlet />}
      showing={showing ? "detail" : "list"}
      listPath={
        openKey
          ? indexUpPath(paths, indexKey, tree.data?.nodes, openKey)
          : paths.index(indexKey)
      }
      listLabel={tree.data?.index.title || m.documents_kind_index()}
    />
  );
}

/** The document screen inside an index: documents open, and new ones go, there. */
function IndexDocumentPlace({
  indexKey,
  children,
}: {
  indexKey: string;
  children: ReactNode;
}) {
  const api = useApi();
  const tree = useQuery(treeQuery(api, indexKey));
  const place = useIndexPlace(indexKey, tree.data?.index.id ?? null);
  return (
    <DocumentPlaceContext.Provider value={place}>
      {children}
    </DocumentPlaceContext.Provider>
  );
}

/** The paths, the home and the place of the list `mount` at `base`. */
function MountScope({
  mount,
  base,
  children,
}: {
  mount: DocumentsMount;
  base: string;
  children: ReactNode;
}) {
  const paths = useMemo(() => documentPaths(base), [base]);
  const place = useMemo(() => listPlace(paths), [paths]);
  return (
    <DocumentPathsContext.Provider value={paths}>
      <DocumentsHomeContext.Provider value={mount.home}>
        <DocumentPlaceContext.Provider value={place}>
          {children}
        </DocumentPlaceContext.Provider>
      </DocumentsHomeContext.Provider>
    </DocumentPathsContext.Provider>
  );
}

const NO_BLOCKS: readonly DocumentBlockPart[] = [];

/** The parts' blocks of a body: given as they are, or loaded on first use. */
interface BlockSource extends Preloadable {
  /** The blocks; suspends while they load. */
  useBlocks(): readonly DocumentBlockPart[];
}

function blockSource(given: DocumentsScreenOptions["blocks"]): BlockSource {
  if (typeof given !== "function") {
    const list = given ?? NO_BLOCKS;
    return { useBlocks: () => list };
  }
  let list: readonly DocumentBlockPart[] | undefined;
  let loading: Promise<void> | undefined;
  const preload = () => {
    loading ??= given().then(
      (loaded) => {
        list = loaded;
      },
      (error: unknown) => {
        loading = undefined;
        throw error;
      },
    );
    return loading;
  };
  return {
    preload,
    useBlocks() {
      if (!list) use(preload());
      return list ?? NO_BLOCKS;
    },
  };
}

/** The body's blocks for what is under it (a document's frame, a side view). */
function WithBlocks({
  source,
  children,
}: {
  source: BlockSource;
  children: ReactNode;
}) {
  return (
    <DocumentBlockPartsContext.Provider value={source.useBlocks()}>
      {children}
    </DocumentBlockPartsContext.Provider>
  );
}

/** The base of the list a document belongs to, when it is not `mount`'s at `here`. */
async function elsewhere(
  c: MeRouterContext,
  mount: DocumentsMount,
  options: DocumentsScreenOptions,
  page: Pick<PageDTO, "id" | "ownerUserId">,
  here: string,
): Promise<string | null> {
  if (mount.holds(page)) return null;
  let base: string | null = null;
  if (page.ownerUserId !== null) base = ME_DOCUMENTS;
  else if (options.placeBase) {
    const ref = (await c.queryClient
      .fetchQuery(refQuery(c.api, "page", page.id))
      .catch(() => null)) as ResolvedRef | null;
    base = ref?.visible && ref.path ? options.placeBase(ref.path) : null;
  }
  return base && base !== here ? base : null;
}

/**
 * The routes of the documents under `parent` at `mount.path`: the list, a new
 * document, a document by key, an index's tree with the documents opened from
 * it and a new index. Me mounts them at /me/documents; a host mounts its own
 * list the same way.
 */
export function documentsRoutes(
  parent: AnyRoute,
  mount: DocumentsMount,
  options: DocumentsScreenOptions,
): AnyRoute[] {
  const blocks = blockSource(options.blocks);
  const documents = createRoute({
    getParentRoute: () => parent,
    path: mount.path,
    component: withLazy(
      function DocumentsRoute() {
        const params = useParams({ strict: false }) as Record<string, string>;
        return (
          <MountScope mount={mount} base={mount.base(params)}>
            <DocumentsScreen
              mount={mount}
              indexDetailRoutes={() => [indexNew.id, indexOpen.id]}
            />
          </MountScope>
        );
      },
      mount.List as Preloadable,
      IndexColumn,
    ),
  });
  // Nothing open: the list alone fills the panel (ListDetail).
  const list = createRoute({
    getParentRoute: () => documents,
    path: "/",
    component: () => null,
  });
  // The frame stays mounted between /new and /$key (see DocumentFrame).
  const frame = createRoute({
    getParentRoute: () => documents,
    id: "document",
    component: withLazy(
      () => (
        <WithBlocks source={blocks}>
          <DocumentFrame
            transport={options.live}
            routes={{ newRouteId: create.id, keyRouteId: open.id }}
          />
        </WithBlocks>
      ),
      DocumentFrame,
      blocks,
    ),
  });
  const create = createRoute({
    getParentRoute: () => frame,
    path: NEW_DOCUMENT_KEY,
    // A new session per visit (the loader runs on each one).
    loader: () => ({ session: crypto.randomUUID() }),
    component: () => null,
  });
  const open = createRoute({
    getParentRoute: () => frame,
    path: "$key",
    loader: async ({ context, params }) => {
      const c = context as MeRouterContext;
      const here = mount.base(params as Record<string, string>);
      // A failure is left to the screen (it says so in the panel).
      const detail = await c.queryClient
        .ensureQueryData(detailQuery(c.api, params.key))
        .catch(() => null);
      if (!detail) return;
      const { page } = detail;
      const other = await elsewhere(c, mount, options, page, here);
      const paths = documentPaths(other ?? here);
      // An index opens as its tree.
      if (page.kind === "index")
        throw redirect({ to: paths.index(page.key), replace: true });
      if (other !== null || page.key !== params.key) {
        c.queryClient.setQueryData(detailKey(page.key), detail);
        throw redirect({ to: paths.document(page.key), replace: true });
      }
    },
    component: () => null,
  });
  // <list>/index/<key>: an index's tree, and the documents opened from it
  // beside it (<list>/index/<key>/<document key>).
  const index = createRoute({
    getParentRoute: () => documents,
    path: "index/$indexKey",
    loader: async ({ context, params }) => {
      if (params.indexKey === NEW_INDEX_KEY) return;
      const c = context as MeRouterContext;
      const here = mount.base(params as Record<string, string>);
      const tree = await c.queryClient
        .ensureQueryData(treeQuery(c.api, params.indexKey))
        .catch(() => null);
      if (!tree) return;
      const other = await elsewhere(c, mount, options, tree.index, here);
      // Named by its id (a favorite): the key's path is the one shared.
      if (other !== null || tree.index.key !== params.indexKey)
        throw redirect({
          to: documentPaths(other ?? here).index(tree.index.key),
          replace: true,
        });
    },
    component: function IndexRoute() {
      const { indexKey } = index.useParams();
      return (
        <IndexDocumentPlace indexKey={indexKey}>
          <Outlet />
        </IndexDocumentPlace>
      );
    },
  });
  const indexAlone = createRoute({
    getParentRoute: () => index,
    path: "/",
    component: () => null,
  });
  const indexFrame = createRoute({
    getParentRoute: () => index,
    id: "index-document",
    component: withLazy(
      () => (
        <WithBlocks source={blocks}>
          <DocumentFrame
            transport={options.live}
            routes={{ newRouteId: indexNew.id, keyRouteId: indexOpen.id }}
          />
        </WithBlocks>
      ),
      DocumentFrame,
      blocks,
    ),
  });
  const indexNew = createRoute({
    getParentRoute: () => indexFrame,
    path: NEW_DOCUMENT_KEY,
    loader: () => ({ session: crypto.randomUUID() }),
    component: () => null,
  });
  const indexOpen = createRoute({
    getParentRoute: () => indexFrame,
    path: "$key",
    loader: async ({ context, params }) => {
      const c = context as MeRouterContext;
      const detail = await c.queryClient
        .ensureQueryData(detailQuery(c.api, params.key))
        .catch(() => null);
      if (detail?.page.kind === "index")
        throw redirect({
          to: documentPaths(mount.base(params as Record<string, string>)).index(
            detail.page.key,
          ),
          replace: true,
        });
    },
    component: () => null,
  });
  return [
    documents.addChildren([
      list,
      frame.addChildren([create, open]),
      index.addChildren([
        indexAlone,
        indexFrame.addChildren([indexNew, indexOpen]),
      ]),
    ]),
  ];
}

const idOf = (payload: unknown): string | null => {
  const id = (payload as { id?: unknown } | null)?.id;
  return typeof id === "string" ? id : null;
};

/** A document's row changed: the lists, its details and references to it. */
function changed(qc: QueryClient, payload: unknown) {
  void qc.invalidateQueries({ queryKey: listPrefix });
  // The trees show the documents' names and leave out the archived ones.
  void qc.invalidateQueries({ queryKey: indexesPrefix });
  const id = idOf(payload);
  if (!id) return;
  invalidateDetailsOf(qc, id);
  void qc.invalidateQueries({ queryKey: refPrefix("page", id) });
}

/** A ui block's answers changed: the document's answers are fetched again. */
function answersChanged(qc: QueryClient, payload: unknown) {
  const pageId = (payload as { pageId?: unknown } | null)?.pageId;
  if (typeof pageId === "string")
    void qc.invalidateQueries({ queryKey: uiAnswersKey(pageId) });
}

/** Me's own documents at /me/documents. */
const ME_MOUNT: DocumentsMount = {
  path: "documents",
  base: () => ME_DOCUMENTS,
  List: DocumentList,
  listLabel: () => m.documents_nav(),
  home: ME_HOME,
  holds: (page) => page.ownerUserId !== null,
};

export function documentsScreen(options: DocumentsScreenOptions): ScreenPart {
  const icon = iconOf("documents");
  const blocks = blockSource(options.blocks);
  return defineScreenPart({
    id: "documents",
    nav: {
      order: 40,
      label: () => m.documents_nav(),
      icon,
      to: ME_DOCUMENTS,
    },
    routes: (main) => [
      ...documentsRoutes(main, ME_MOUNT, options),
      // One's own datasets go with the documents (their list opens from its header).
      ...meDatasetsRoutes(main),
    ],
    realtime: {
      "page.created": (_p, { qc }) => {
        void qc.invalidateQueries({ queryKey: listPrefix });
        void qc.invalidateQueries({ queryKey: indexListKey });
      },
      // An index's tree changed (here, in another window, by AI).
      "index.changed": (_p, { qc }) =>
        void qc.invalidateQueries({ queryKey: indexesPrefix }),
      // These change what a reference to the document shows (name, missing) too.
      "page.renamed": (payload, { qc }) => changed(qc, payload),
      "page.archived": (payload, { qc }) => changed(qc, payload),
      "page.unarchived": (payload, { qc }) => changed(qc, payload),
      "page.deleted": (payload, { qc }) => changed(qc, payload),
      // One's own document went to a shared place: it leaves the list and its
      // indexes, and its details say who may write it now.
      "page.released": (payload, { qc }) => changed(qc, payload),
      "page.revision": (payload, { qc }) => {
        // The list is ordered by the last change.
        void qc.invalidateQueries({ queryKey: listPrefix });
        const id = idOf(payload);
        if (!id) return;
        void qc.invalidateQueries({ queryKey: versionsPrefix(id) });
        const revision = (payload as { revision?: unknown }).revision;
        if (typeof revision === "number") noteRevision(qc, id, revision);
        else invalidateDetailsOf(qc, id);
      },
      // Someone's answer to a ui block (the viewer's own too: it settles the overlay).
      "ui_answer.saved": (payload, { qc }) => answersChanged(qc, payload),
      "ui_answer.cleared": (payload, { qc }) => answersChanged(qc, payload),
      // One's own AI memo on a document (by AI or the dialog): whether the "…" offers it.
      "agent_memo.changed": (payload, { qc }) => {
        const { kind, id } = (payload ?? {}) as {
          kind?: unknown;
          id?: unknown;
        };
        if (kind !== "page" || typeof id !== "string") return;
        void invalidateMemo(qc, "page", id);
        invalidateDetailsOf(qc, id);
      },
    },
    // A favorite and a reference name the document's id; its route turns it
    // into the key's path (and sends a shared one to its list). A reference
    // that says where a shared document is opens there at once. A pasted
    // document URL becomes a reference.
    favoriteKinds: {
      page: {
        icon,
        to: (id, path) => {
          const base = path ? options.placeBase?.(path) : null;
          if (!base) return documentPath(id);
          const key = typeof path?.key === "string" ? path.key : id;
          return documentPaths(base).document(key);
        },
        refPaths: [...ME_DOCUMENT_REF_PATHS, ...(options.refPaths ?? [])],
      },
      // One's own dataset opens its screen beside the documents (a host
      // with places of its own opens theirs there).
      dataset: {
        icon: iconOf("dataset"),
        to: (id) => meDatasetPath(id),
        refPaths: ME_DATASET_REF_PATHS,
      },
    },
    sideViews: {
      page: function DocumentSideView({ id }: SideViewProps) {
        return (
          <WithBlocks source={blocks}>
            <DocumentView docKey={id} transport={options.live} />
          </WithBlocks>
        );
      },
    },
    // What cloud draws right after a reload: the list and the open document.
    persist: (key) =>
      key[0] === documentsPrefix[0] &&
      key[1] === documentsPrefix[1] &&
      (key[2] === "list" || key[2] === "detail"),
  });
}
